// hooks/useLoginSubmit.js
import {useState} from 'react'
import {useIntl, defineMessage} from 'react-intl'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerBaskets,
    useCustomerId,
    useCustomerType,
    useShopperBasketsMutation,
    useAccessToken
} from '@salesforce/commerce-sdk-react'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {
    API_ERROR_MESSAGE,
    CREATE_ACCOUNT_FIRST_ERROR_MESSAGE,
    FEATURE_UNAVAILABLE_ERROR_MESSAGE,
    LOGIN_TYPES,
    PASSWORDLESS_ERROR_MESSAGES,
    USER_NOT_FOUND_ERROR
} from '@salesforce/retail-react-app/app/constants'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils'
import {setSessionJSONItem} from '@salesforce/retail-react-app/app/utils/utils'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import {useBrand} from '../contexts/brand-context'

import {sendLoginFailedEvent, sendViewErrorEvent} from '../utils/gtm-utils'

import {useAuthAuxHelper} from './use-auth-aux-helper'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

const LOGIN_ERROR_MESSAGE_GARAGE = defineMessage({
    defaultMessage: 'Your email address or password is incorrect.',
    id: 'login_page.error.incorrect_username_or_password.garage'
})

const LOGIN_ERROR_MESSAGE_DYNAMITE = defineMessage({
    defaultMessage: 'Your email address or password is incorrect.',
    id: 'login_page.error.incorrect_username_or_password.dynamite'
})

const ACCOUNT_VERIFIED_MESSAGE_GARAGE = defineMessage({
    defaultMessage: 'Your account needs to be verified. Please check your email.',
    id: 'login_page.error.account_not_verified.garage'
})

const ACCOUNT_VERIFIED_MESSAGE_DYNAMITE = defineMessage({
    defaultMessage: 'Your account needs to be verified. Please check your email.',
    id: 'login_page.error.account_not_verified.dynamite'
})

const EMAIL_VIEW = 'email'

/**
 * Custom hook for handling login submission logic across different contexts
 *
 * Supports password login, passwordless login, and guest checkout.
 * Works in both login page and checkout contexts.
 *
 * @param {Object} form - React Hook Form instance
 * @param {Object} options - Configuration options
 * @param {Function} options.onLoginSuccess - Called when login succeeds
 * @param {Function} options.onPasswordlessSuccess - Called when passwordless login request succeeds
 * @param {Function} options.onSetCurrentView - Called to change current view
 * @param {Function} options.onError - Called when an error occurs
 * @param {Function} options.onAccountNotVerified - Called when account verification is required
 * @param {Function} options.onCloseModal - Called to close open modal
 * @param {string} options.context - Context ('login' or 'checkout')
 * @param {string} options.callbackURI - Callback URI for passwordless login
 * @returns {Object} Hook methods and state
 */
const useLoginSubmit = (form, options = {}) => {
    const {formatMessage} = useIntl()
    const {customerType} = useCustomerType()
    const login = useAuthAuxHelper(AuthHelpers.LoginRegisteredUserB2C)
    const authorizePasswordlessLogin = useAuthHelper(AuthHelpers.AuthorizePasswordless)
    const customerId = useCustomerId()
    const prevAuthType = usePrevious(customerType)
    const navigate = useNavigation()
    const {isGarage} = useBrand()

    // Get baskets for merge functionality
    const {data: baskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId, keepPreviousData: true}
    )

    // For checkout context - get current basket
    const {data: currentBasket} = useCurrentBasket()

    const mergeBasket = useShopperBasketsMutation('mergeBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()

    const [passwordlessLoginEmail, setPasswordlessLoginEmail] = useState('')
    const [loginType, setLoginType] = useState(LOGIN_TYPES?.PASSWORD || 'password')
    const {executeRecaptcha} = useGoogleReCaptcha()

    // Optional callbacks that can be passed via options
    const {
        onLoginSuccess,
        onPasswordlessSuccess,
        onSetCurrentView,
        onError,
        onAccountNotVerified,
        onCloseModal = () => {},
        context = 'login', // 'login' or 'checkout'
        callbackURI
    } = options

    /**
     * Get passwordless callback URL with current path for redirect
     * @private
     */
    const getPasswordlessCallbackURL = () => {
        const passwordlessConfigCallback = getConfig().app.login?.passwordless?.callbackURI
        const baseCallbackURL = isAbsoluteURL(passwordlessConfigCallback)
            ? passwordlessConfigCallback
            : `${getAppOrigin()}${passwordlessConfigCallback}`

        if (callbackURI) {
            return callbackURI
        }

        // Add current path for redirect after passwordless login
        if (typeof window !== 'undefined') {
            const redirectPath = window.location.pathname + window.location.search
            return `${baseCallbackURL}?redirectUrl=${redirectPath}`
        }

        return baseCallbackURL
    }

    /**
     * Process SFID login
     * @private
     */
    const processSFIDLogin = async (email, password) => {
        // Get reCAPTCHA token
        let recaptchaToken = ''
        if (executeRecaptcha) {
            try {
                recaptchaToken = await executeRecaptcha('login')
            } catch (error) {
                console.error('reCAPTCHA error:', error)
            }
        } else {
            console.warn('reCAPTCHA not available - proceeding without verification')
        }

        const token = await getTokenWhenReady()

        const response = await fetch(`${appOrigin}/process-login`, { 
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                token,
                username: email,
                password: password,
                recaptchaToken
            })
        })

        if (!response.ok) {
            // Check if it's account verification error
            if (response.status === 403) {
                throw new Error('ACCOUNT_NOT_VERIFIED')
            }
            throw new Error('SFID login failed')
        }

        const loginData = await response.json()

        if (!loginData?.password) {
            throw new Error('Invalid SFID login response')
        }

        return loginData
    }

    /**
     * Handles basket merging after successful login
     * @private
     */
    const handleMergeBasket = () => {
        try {
            // For checkout context, use currentBasket; for login page, use baskets
            const basketToCheck = context === 'checkout' ? currentBasket : baskets?.baskets?.[0]
            const hasBasketItem = basketToCheck?.productItems?.length > 0
            const shouldMergeBasket = hasBasketItem && prevAuthType === 'guest'

            if (shouldMergeBasket) {
                mergeBasket.mutate({
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    parameters: {
                        createDestinationBasket: true
                    }
                })
            }
        } catch (e) {
            console.error('Error merging basket:', e)
            if (form?.setError) {
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage(API_ERROR_MESSAGE)
                })
                // GTM Modification
                sendViewErrorEvent(API_ERROR_MESSAGE.defaultMessage)
            }
        }
    }

    /**
     * Handles passwordless login flow
     * @param {string} email - User email
     * @private
     */
    const handlePasswordlessLogin = async (email) => {
        try {
            const params = {userid: email}

            // Add callback URI
            const callbackURL = getPasswordlessCallbackURL()
            if (callbackURL) {
                params.callbackURI = callbackURL
            }

            await authorizePasswordlessLogin.mutateAsync(params)
            setPasswordlessLoginEmail(email)

            if (onSetCurrentView) {
                onSetCurrentView(EMAIL_VIEW)
            }
            if (onPasswordlessSuccess) {
                onPasswordlessSuccess(email)
            }
        } catch (error) {
            const message = USER_NOT_FOUND_ERROR?.test(error.message)
                ? formatMessage(CREATE_ACCOUNT_FIRST_ERROR_MESSAGE)
                : PASSWORDLESS_ERROR_MESSAGES?.some((msg) => msg.test(error.message))
                ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                : formatMessage(API_ERROR_MESSAGE)

            // Set form error or call error callback
            if (onError) {
                onError(message)
            } else if (form?.setError) {
                form.setError('global', {type: 'manual', message})
                // GTM Modification
                sendViewErrorEvent(message)
            }
        }
    }

    /**
     * Handles guest checkout flow
     * @param {string} email - User email
     * @private
     */
    const handleGuestCheckout = async (email) => {
        try {
            if (currentBasket?.basketId) {
                await updateCustomerForBasket.mutateAsync({
                    parameters: {basketId: currentBasket.basketId},
                    body: {email}
                })
                if (onLoginSuccess) {
                    onLoginSuccess({email, isGuest: true})
                }
            }
        } catch (error) {
            const message = formatMessage(API_ERROR_MESSAGE)
            if (onError) {
                onError(message)
            } else if (form?.setError) {
                form.setError('global', {type: 'manual', message})
                // GTM Modification
                sendViewErrorEvent(message)
            }
        }
    }

    /**
     * Handles regular password login with SFID modification
     * @param {Object} data - Form data with email and password
     * @private
     */
    const handlePasswordLogin = async (data) => {
        try {
            // Process SFID login first
            const sfidData = await processSFIDLogin(data.email, data.password)

            // Then proceed with Commerce API login
            await login.mutateAsync({
                credentials: {
                    username: sfidData.username,
                    password: sfidData.password
                },
                body: {
                    ...sfidData.body,
                    rememberme: data.rememberme
                }
            })

            handleMergeBasket()

            setSessionJSONItem('from-login', true)

            if (onLoginSuccess) {
                onLoginSuccess(data)
            }
        } catch (error) {
            let message = isGarage
                ? formatMessage(LOGIN_ERROR_MESSAGE_GARAGE)
                : formatMessage(LOGIN_ERROR_MESSAGE_DYNAMITE)

            try {
                const errorData = await error.response?.json()
                const errorMessage = JSON.parse(errorData.message)

                if (errorMessage.statusCode === '403') {
                    message = isGarage
                        ? formatMessage(ACCOUNT_VERIFIED_MESSAGE_GARAGE)
                        : formatMessage(ACCOUNT_VERIFIED_MESSAGE_DYNAMITE)

                    if (onAccountNotVerified) {
                        onAccountNotVerified(data)
                        onCloseModal()
                    } else {
                        navigate('/email-confirm')
                    }
                    return
                }
            } catch (parseError) {
                console.error('Error parsing response:', parseError)
            }

            if (onError) {
                onError(message)
            } else if (form?.setError) {
                form.setError('global', {type: 'manual', message})
                // GTM Modification
                sendViewErrorEvent(message)
            }

            // GTM Modification
            sendLoginFailedEvent('email_password')
        }
    }

    /**
     * Main form submission handler
     * @param {Object} data - Form data
     * @param {string} currentView - Current view ('login' or 'email')
     * @param {Object} submitOptions - Additional options
     */
    const submitForm = async (data, currentView = 'login', submitOptions = {}) => {
        // Clear errors first
        form?.clearErrors()

        const {
            isPasswordlessLoginClicked = false,
            isGuestCheckout = false,
            loginTypeOverride
        } = submitOptions

        // Determine effective login type (allow override)
        const effectiveLoginType = loginTypeOverride || loginType

        // Handle email confirmation view
        if (currentView === 'email') {
            return await handlePasswordlessLogin(passwordlessLoginEmail)
        }

        // Handle login view with different flows
        if (currentView === 'login') {
            // 1. Handle passwordless login (explicit click or type)
            if (effectiveLoginType === LOGIN_TYPES.PASSWORDLESS || isPasswordlessLoginClicked) {
                return await handlePasswordlessLogin(data.email)
            }

            // 2. Handle guest checkout (only in checkout context)
            if (context === 'checkout' && (!data.password || isGuestCheckout)) {
                return await handleGuestCheckout(data.email)
            }

            // 3. Handle regular password login (default for login context)
            if (data.password && effectiveLoginType === LOGIN_TYPES.PASSWORD) {
                return await handlePasswordLogin(data)
            }

            // 4. Fallback to guest checkout if in checkout context
            if (context === 'checkout') {
                return await handleGuestCheckout(data.email)
            }
        }

        // If we get here, no valid flow was found
        console.warn('No valid login flow found for:', {currentView, effectiveLoginType, context})
    }

    return {
        submitForm,
        handleMergeBasket,
        passwordlessLoginEmail,
        setPasswordlessLoginEmail,
        setLoginType,
        loginType,
        isLoading: login.isLoading || authorizePasswordlessLogin.isLoading
    }
}

export {useLoginSubmit}
